
import React, { useState } from "react";
import "../css/FairPracticesCode.css";
import { Link } from "react-router-dom";
import Header from "../component/Header";
import Footer from "../component/Footer";
import { motion } from "framer-motion";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);

  return (
    <div className="section">
      <div className="section-header" onClick={() => setOpen(!open)}>
        <h2>{title}</h2>
        <span>{open ? "−" : "+"}</span>
      </div>

      {open && <div className="section-body">{children}</div>}
    </div>
  );
};

export default function FairPracticesCode() {
  return (
    <>
      <Header />

      <section className="relative h-[300px] flex items-center justify-center bg-gradient-to-br from-[#0b1f3a] to-[#0a2540] overflow-hidden">

        {/* Background Glow */}
        <div className="absolute w-[400px] h-[400px] bg-blue-500/20 rounded-full blur-3xl animate-pulse top-[-100px] left-[-100px]" />

        <div className="absolute w-[300px] h-[300px] bg-orange-400/20 rounded-full blur-3xl animate-pulse bottom-[-80px] right-[-80px]" />

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 px-6 text-center"
        >
          {/* Breadcrumb */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-300 mb-3 text-sm md:text-base"
          >
            Home &nbsp; / &nbsp; Fair Practices Code
          </motion.p>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-5xl font-bold text-white"
          >
            Fair{" "}
            <span className="text-orange-400">
              Practices Code
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-4 text-gray-200 text-sm md:text-base max-w-2xl mx-auto"
          >
            We are committed to providing a transparent and accessible
            mechanism for addressing customer.
          </motion.p>
        </motion.div>
      </section>

      <div className="fpc-container">

        {/* Preamble */}
        <Section title="1. Preamble">
          <p>
            The Fair Practices Code (“FPC”) has been formulated and approved by
            the Board of Directors of Kisga Leasing and Finance Pvt Ltd
            (“Company”) in accordance with the Master Direction – Reserve Bank
            of India (Non-Banking Financial Company – Scale Based Regulation)
            Directions, 2023 issued by the Reserve Bank of India (“RBI”)
            (“RBI Master Directions”), as amended from time to time.
            <br />
            <br />

            In accordance with Chapter VII (Fair Practices Code) of the RBI
            Master Directions, Non-Banking Financial Companies (“NBFCs”) having
            customer interface are required to adopt the guidelines on Fair
            Practice Code (‘FPC’) prescribed in the RBI Master Directions.
            The Company, being an NBFC having customer interface, has adopted
            this FPC for the lending business. The FPC also seeks to incorporate
            various disclosure requirements prescribed by the guidelines /
            directions issued by Reserve Bank of India on Digital Lending, and
            as amended from time to time, as applicable to the Company’s lending
            business.
          </p>
        </Section>

        {/* Purpose */}
        <Section title="2. Purpose & Applicability">
          <p>
            The Company has adopted the FPC for implementation with an endeavor
            to achieve fair and transparent practices while dealing with its
            customers/ borrowers. This FPC intends to promote good and fair
            practices by setting minimum standards to be followed while dealing
            with the customers. Further, the FPC also seeks to increase
            transparency so that the customers can have better understanding of
            the products/ services being offered by the Company.
            <br />
            <br />

            This FPC shall apply across all aspects of the Company’s lending
            business operations including digital lending, marketing, loan
            origination, processing, servicing, collection activities etc. The
            Company’s commitment to the FPC would be demonstrated in terms of
            employee accountability, monitoring and auditing programs, training
            and technology.
            <br />
            <br />

            The Company’s Board of Directors and the management are responsible
            for establishing practices designed to ensure that its operations
            reflect a strong commitment to the FPC and that all employees are
            aware of the FPC.
          </p>
        </Section>

        {/* Definitions */}
        <Section title="3. Definitions">
          <ul>
            <li>
              <strong>Authorised Representative:</strong> “A person other than
              an Advocate duly appointed and authorised in writing to represent
              a complainant in the proceedings before the officials of RBI;
            </li>

            <li>
              <strong>Board:</strong> Board of Directors of the Company.
            </li>

            <li>
              <strong>Complaint:</strong> Any representation made in writing or
              through other modes alleging deficiency in service on the part of
              the Company, and/ or seeking relief under the Scheme.
            </li>

            <li>
              <strong>Company:</strong> Kisga Leasing and Finance Pvt Ltd.
            </li>

            <li>
              <strong>Director:</strong> An individual Director or any of the
              Directors on the Board of the Company.
            </li>

            <li>
              <strong>FPC:</strong> Fair Practices Code.
            </li>

            <li>
              <strong>RBI:</strong> Reserve Bank of India.
            </li>
          </ul>
        </Section>

        {/* Norms */}
        <Section title="4. Norms Applicable to All Loan Products">

          <h4>Applications for Loans and Processing</h4>

          <ul>
            <li>
              All loan products of the Company shall be as per the Loan Policy
              adopted by the Company.
            </li>

            <li>
              All communications to the customer by the Company shall be in
              English as it is commonly understood by all the customers of the
              Company. If a customer explicitly requests communication in
              another language, the Company will acknowledge this preference,
              and all subsequent communications with that client will be in the
              requested language.
            </li>

            <li>
              As part of the process and to ensure transparency, the Company,
              at the application stage, shall provide all necessary information
              including but not restricted to processing fees/charges, if any,
              non-refundable fees in case of rejection of loan proposal,
              pre-payment options etc., which affect the interest of the
              customer so that a meaningful comparison with the terms and
              conditions offered by other NBFCs can be made and informed
              decision can be taken by the customer.
            </li>

            <li>
              The Company shall provide acknowledgment for receipt of loan
              applications along with the time frame within which loan
              applications would be disposed-of.
            </li>

            <li>
              The Company, at loan application stage, shall indicate all the
              documents required to be submitted along with the application
              form.
            </li>

            <li>
              On exercise of choice, the customer would be given the relevant
              information about the loan product of his/her choice.
            </li>

            <li>
              The customer would be informed about the status of his/ her
              application, as and when required. The Company, while accepting
              loan application shall explain the entire loan process and journey
              till the sanction and disbursement of loan. The customer shall
              also be informed about the timelines within which the entire loan
              process will be completed in the ordinary course of business.
            </li>
          </ul>

          <h4>Loan Appraisal Terms & Condition</h4>

          <ul>
            <li>
              The Company shall scrutinize the information submitted by the
              customer and, if any additional data is required, it shall seek
              the same promptly to facilitate expeditious disposal of the loan
              application.
            </li>

            <li>
              The Company shall convey key terms and conditions of the proposed
              loan in writing (in English as it is understood by all customers
              of the Company), by means of Sanction Letter or any other form of
              written communication and the same shall include:
            </li>

            <li>
              The amount of loan sanctioned along with the terms and conditions,
              including the annualized rate of interest.
            </li>

            <li>
              Details of the default interest / overdue charges (expressed in
              percentage per month/annum, as the case may be) and the charges
              payable by the customers in relation to their loan account and
              the method of application thereof.
            </li>

            <li>
              Overdue charges for late repayment of loan shall be expressly
              mentioned in bold in the loan agreement.
            </li>

            <li>
              There shall be no capitalisation of overdue charges charged by
              the Company to the borrower, i.e., no further interest will be
              computed on such overdue charges.
            </li>

            <li>
              The customer shall provide explicit acceptance of the terms and
              conditions prior to availing of the loan.
            </li>

            <li>
              The quantum and reason for overdue charges shall be clearly
              disclosed by the Company to the customers in the loan agreement
              and the most important terms &amp; conditions/Key Fact Statement
              (KFS), in addition to being displayed on the website of the
              Company under Interest Rates and Service Charges.
            </li>

            <li>
              If the Company cannot provide the loan to the applicant, it shall
              endeavour to convey the reason(s) for rejection.
            </li>

            <li>
              The Company shall furnish a copy of the loan agreement in English
              as understood by the borrower, along with copies of all relevant
              enclosures quoted in the loan agreement, to all borrowers at the
              time of sanction/disbursement of the loan. The same shall be duly
              approved by the customer and countersigned by the authorized
              officials of the Company.
            </li>
          </ul>

          <h4>Terms &amp; Conditions</h4>

          <ul>
            <li>
              The Company shall give notice in English as understood by the
              customer regarding any change in the terms and conditions,
              including disbursement schedule, interest rates, service charges,
              prepayment charges, etc.
            </li>

            <li>
              The penal charges to be charged by the Company for late repayment
              of loan shall be mentioned in bold letters in the loan agreement.
            </li>

            <li>
              Any decision to recall or accelerate payment or performance under
              the agreement shall be in consonance with the terms of the loan
              agreement.
            </li>
          </ul>

          <h4>Other Terms</h4>

          <ul>
            <li>
              If the customer does not adhere to the repayment schedule, a
              defined process in accordance with the applicable laws will be
              followed for recovery of dues. The process will involve reminding
              the customer by sending notices or by making personal visits. In
              case of default, the Company may refer the case to a recovery
              agent and shall inform the customer of the recovery proceedings
              being initiated. The Company shall ensure that its recovery
              process does not involve any harassment of the customer. The
              Company shall also ensure that all recovery agents engaged by it
              undergo proper due diligence, background verification, and
              periodic training on RBI guidelines, customer protection, and
              ethical recovery practices. Appropriate instructions shall be
              provided by the Company to its staff for handling customer queries
              and grievances cordially.
            </li>

            <li>
              The Company shall not charge foreclosure charges or pre-payment
              penalties on any loans sanctioned to individual borrowers.
            </li>

            <li>
              All fees, charges, and interest shall be levied strictly in
              accordance with the interest rate policy adopted by the Company
              and as per the terms and conditions applicable to the loan.
            </li>

            <li>
              The Company shall consider genuine cases of financial difficulty
              appropriately. The customer shall identify any such difficulty
              and inform the Company as soon as possible.
            </li>

            <li>
              All personal information of the customer shall be kept
              confidential and shall not be disclosed to any third party unless
              agreed to by the customer in writing. Customer data shall be
              collected, processed, and shared strictly on the basis of explicit
              customer consent and in compliance with applicable RBI directions
              and data protection laws.
            </li>

            <li>
              The term “Third Party” excludes law enforcement agencies, Credit
              Information Bureaus, RBI, other banks and financial institutions,
              and any other state, central, or regulatory authority.
            </li>

            <li>
              The Company may reveal customer information under the following
              circumstances:
            </li>

            <li>
              If the Company is compelled by law.
            </li>

            <li>
              If it is in the public interest to disclose such information.
            </li>

            <li>
              If the interest of the Company requires such disclosure.
            </li>
          </ul>

          <h4>Responsibility of Board of Directors</h4>

          <ul>
            <li>
              The Company, with the approval of its Board of Directors, has laid
              down Grievance Redressal Mechanism (“GRM”) within the organization
              as per details mentioned in the next paragraph. Such a mechanism
              ensures that all disputes arising out of the decisions of the
              Company’s functionaries are heard and disposed-off at least at the
              next higher level. The Board of Directors shall annually review
              the compliance of the FPC and the functioning of the GRM. A
              consolidated report in this regard shall be submitted to the Board
              every year.
            </li>
          </ul>
        </Section>

        {/* Grievance Redressal */}
        <Section title="5. Grievance Redressal Mechanism (GRM)">

          <li>
            The Company, with the approval of its Board of Directors, shall
            appoint the Grievance Redressal Officer as Nodal Officer. Below is
            the Grievance Redressal Mechanism approved by the Board of Directors
            of the Company.
          </li>

          <table className="fpc-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Contact Details</th>
                <th>Description</th>
              </tr>
            </thead>

            <tbody>

              <tr>
                <td>
                  <strong>First Escalation - Customer Care</strong>
                </td>

                <td>
                  Phone: +91 8049368211
                  <br />
                  Email: care@b4salary.com
                </td>

                <td>
                  If the issue is not resolved within 7 days of raising the
                  issue, the customer shall raise the issue with the Grievance
                  Officer.
                </td>
              </tr>

              <tr>
                <td>
                  <strong>
                    Second Escalation – Grievance Officer
                  </strong>
                </td>

                <td>
                  Name: Sangeeta Chauhan
                  <br />
                  Phone: 8796553277
                  <br />
                  Email: grievance@b4salary.com
                </td>

                <td>
                  If the issue is not resolved within 7 days of raising the
                  issue, the customer shall raise the further issue with the
                  Nodal Officer.
                </td>
              </tr>

              <tr>
                <td>
                  <strong>
                    Third Escalation – Nodal Officer
                  </strong>
                </td>

                <td>
                  Name: V.K. Gupta 
                  <br />
                  Phone: 7733866660
                  <br />
                  Email: nodalofficer@kisga.com
                </td>

                <td>-</td>
              </tr>

            </tbody>
          </table>

          <li>
            If the customer is not satisfied with the resolution, they may
            approach the RBI Integrated Ombudsman Scheme, as applicable. All
            grievances shall be resolved within a maximum period of 30 days from
            the date of receipt, unless otherwise specified by RBI.
          </li>

          <li>
            <strong>Language and Mode of Communicating the FPC</strong> The
            Company, in accordance with the Guidelines on FPC and RBI Master
            Directions, shall put in place the FPC in English language. The
            Company shall also make the FPC available in regional/ vernacular
            languages for the customers who request for the FPC in vernacular
            language.
          </li>

        </Section>

        {/* Digital Lending */}
        <Section title="6. ADDITIONAL NORMS FOR DIGITAL LENDING OR THE LOANS SOURCED OVER A DIGITAL LENDING PLATFORM">

          <p>
            <br />

            <strong>
              Norms for Loans Sourced by the Company over Digital Lending
              Platform/ Apps (“DLAs”)
            </strong>{" "}
            - In case, the Company sources borrowers and/ or recovers dues over
            a digital lending platform (irrespective of whether they lend
            through their own digital lending platform or through an outsourced
            lending platform), the Company shall abide by this FPC in letter and
            spirit and in the manner it may be applicable to its business.
            Further, the Company shall follow the following instructions:
          </p>

          <ul>
            <li>
              Names of digital lending platforms engaged as agents shall be
              disclosed on the website of the Company.
            </li>

            <li>
              Digital lending platforms engaged as agents shall be directed to
              disclose upfront to the customer the name of the Company on whose
              behalf they are interacting with him.
            </li>

            <li>
              Immediately after sanction but before execution of the loan
              agreement, the sanction letter shall be issued to the borrower on
              the letterhead of the Company.
            </li>

            <li>
              A copy of the loan agreement along with a copy of all enclosures
              quoted in the loan agreement shall be furnished to all borrowers
              at the time of sanction/disbursement of loans.
            </li>

            <li>
              Effective oversight and monitoring shall be ensured over the
              digital lending platforms engaged by the Company.
            </li>

            <li>
              Adequate efforts shall be made towards creation of awareness about
              the grievance redressal mechanism.
            </li>
          </ul>

          <p>
            <br />

            <strong>
              Norms to be followed by the Company with respect to Digital
              Lending
            </strong>{" "}
            – The Company shall comply with the provisions of Reserve Bank of
            India (Digital Lending) Directions, 2025 (as amended from time to
            time) to the extent applicable.
          </p>

        </Section>

        {/* Review */}
        <Section title="7. Review of FPC">

          <p>
            The FPC shall be amended or modified with approval of the Board. The
            FPC shall be reviewed by the Board on an annual basis. Consequent
            upon any amendments in RBI Master Directions or any change in the
            position of the
            <br />
            <br />

            Notwithstanding anything contained in this FPC, in case of any
            contradiction of the provision of this FPC with any existing
            legislations, rules, regulations, laws or modification thereof or
            enactment of a new applicable law, the provisions under such law,
            legislation, rules, regulation or enactment shall prevail over this
            FPC.
          </p>

        </Section>

      </div>

      <Footer />
    </>
  );
}

